// src/pages/Companies.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Search, Plus, RefreshCw, Building2, MapPin, Globe, Phone, Eye, Trash2, Users, ShieldCheck, CheckSquare, Square, X, LayoutGrid, List } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { PhoneLink } from '../utils/helpers';


const STATUS_META = {
  CLIENT:                { color:'#16A34A', bg:'rgba(34,197,94,0.10)',    label:'Client'                },
  ANCIEN_CLIENT:         { color:'var(--text-muted)', bg:'var(--bg-elevated)', label:'Ancien client'    },
  PROSPECT:              { color:'#F59E0B', bg:'rgba(245,158,11,0.10)',   label:'Prospect'              },
  PROSPECT_CHAUD:        { color:'#EA580C', bg:'rgba(234,88,12,0.10)',    label:'Prospect chaud'        },
  PARTENAIRE:            { color:'#3B82F6', bg:'rgba(59,130,246,0.12)',        label:'Partenaire'            },
  ANCIEN_PARTENAIRE:     { color:'#94A3B8', bg:'var(--bg-elevated)',       label:'Ancien partenaire'     },
  FOURNISSEUR:           { color:'#A78BFA', bg:'rgba(139,92,246,0.10)',    label:'Fournisseur'           },
  COLLABORATEUR_EXTERNE: { color:'#06B6D4', bg:'rgba(8,145,178,0.10)',     label:'Collab. externe'       },
  ARCHIVE:               { color:'var(--text-muted)', bg:'var(--bg-elevated)', label:'Archivé'           },
};
const STATUSES = Object.keys(STATUS_META);
const PAGE_SIZE = 9;


export default function Companies() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const isSuperAdmin = (user?.role || '').toUpperCase() === 'SUPER_ADMIN';
  const [items,      setItems]     = useState([]);
  const [selected,   setSelected]  = useState(new Set());
  const [showBulk,   setShowBulk]  = useState(false);
  const [iaList,     setIaList]    = useState([]);
  const [selIAs,     setSelIAs]    = useState([]);
  const [granting,   setGranting]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [page,     setPage]     = useState(1);
  const [viewMode, setViewMode] = useState('grid');


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await companiesAPI.list(params);
      setItems(res.data || []);
      setPage(1);
    } catch { toast.error('Erreur chargement sociétés'); }
    finally { setLoading(false); }
  }, [search, status]);


  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/admin/users/ta-list').then(r => {
      setIaList((r.data || []).filter(u =>
        ['BUSINESS_MANAGER','INGENIEUR_AFFAIRES'].includes((u.role||'').toUpperCase())
      ));
    }).catch(() => {});
  }, [isSuperAdmin]);


  const handleBulkGrant = async () => {
    if (!selIAs.length || !selected.size) { toast.error('Sélectionnez des sociétés et des IA'); return; }
    setGranting(true);
    try {
      await api.post('/companies/bulk-grant-ia-access', {
        company_ids: Array.from(selected),
        user_ids: selIAs,
      });
      toast.success(`Accès accordés à ${selIAs.length} IA(s) pour ${selected.size} société(s)`);
      setSelected(new Set()); setSelIAs([]); setShowBulk(false);
    } catch { toast.error('Erreur attribution'); }
    finally { setGranting(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(c => c.id)));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette société ?')) return;
    try { await companiesAPI.delete(id); toast.success('Société supprimée'); fetchData(); }
    catch { toast.error('Erreur suppression'); }
  };


  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>CRM — Sociétés</h1>
          <p>Clients, prospects et partenaires de l'ESN</p>
        </div>
        <div className="page-header-actions">
          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            <button onClick={() => setViewMode('grid')} style={{ padding:'6px 10px', background: viewMode==='grid' ? '#3B82F6' : 'var(--bg-elevated)', color: viewMode==='grid' ? '#fff' : 'var(--text-muted)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', transition:'all 0.15s' }}><LayoutGrid size={15}/></button>
            <button onClick={() => setViewMode('list')} style={{ padding:'6px 10px', background: viewMode==='list' ? '#3B82F6' : 'var(--bg-elevated)', color: viewMode==='list' ? '#fff' : 'var(--text-muted)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', transition:'all 0.15s' }}><List size={15}/></button>
          </div>
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
          <button className="btn btn-primary" onClick={() => navigate('/companies/new')}>
            <Plus size={15}/> Nouvelle société
          </button>
        </div>
      </div>


      <div className="filters-bar">
        <div className="filter-search">
          <Search size={15} color="var(--text-muted)"/>
          <input placeholder="Nom, secteur, ville…" value={search}
            onChange={e => setSearch(e.target.value)}/>
        </div>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
        </select>
        {(search || status) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatus(''); }}>
            Réinitialiser
          </button>
        )}
      </div>


      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>
          {loading ? 'Chargement…' : `${items.length} société${items.length !== 1 ? 's' : ''}${totalPages > 1 ? ` — page ${page}/${totalPages}` : ''}`}
          {selected.size > 0 && <span style={{ marginLeft:10, fontWeight:700, color:'#3B82F6' }}>{selected.size} sélectionnée{selected.size > 1 ? 's' : ''}</span>}
        </div>
        {isSuperAdmin && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {items.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={toggleSelectAll}>
                {selected.size === items.length ? <CheckSquare size={14}/> : <Square size={14}/>}
                {selected.size === items.length ? ' Tout désélectionner' : ' Tout sélectionner'}
              </button>
            )}
            {selected.size > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowBulk(s => !s)}>
                <ShieldCheck size={14}/> Attribuer à des IA ({selected.size})
              </button>
            )}
          </div>
        )}
      </div>

      {/* Panel attribution bulk */}
      {isSuperAdmin && showBulk && selected.size > 0 && (
        <div className="card" style={{ marginBottom:16, border:'2px solid #3B82F6' }}>
          <div className="card-header" style={{ background:'rgba(59,130,246,0.08)' }}>
            <span className="card-title" style={{ color:'#1D4ED8' }}>
              <ShieldCheck size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>
              Attribuer {selected.size} société{selected.size > 1 ? 's' : ''} à des IAs
            </span>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowBulk(false)}><X size={14}/></button>
          </div>
          <div style={{ padding:'14px 16px' }}>
            <label style={{ display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:8, textTransform:'uppercase' }}>
              Sélectionner les IA
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:8, marginBottom:14 }}>
              {iaList.map(u => {
                const sel = selIAs.includes(u.id);
                return (
                  <label key={u.id} style={{
                    display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
                    border:`2px solid ${sel ? '#3B82F6' : 'var(--border)'}`,
                    borderRadius:8, cursor:'pointer',
                    background: sel ? 'rgba(59,130,246,0.08)' : 'var(--bg-elevated)',
                  }}>
                    <input type="checkbox" checked={sel}
                      onChange={() => setSelIAs(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])}/>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.8125rem' }}>{u.prenom} {u.nom}</div>
                      <div style={{ fontSize:'0.6875rem', color:'var(--text-muted)' }}>{u.email}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowBulk(false); setSelIAs([]); }}>Annuler</button>
              <button className="btn btn-primary btn-sm" disabled={granting || !selIAs.length} onClick={handleBulkGrant}>
                {granting ? <div className="spinner" style={{ width:12, height:12 }}/> : <ShieldCheck size={13}/>}
                Confirmer ({selIAs.length} IA{selIAs.length > 1 ? 's' : ''})
              </button>
            </div>
          </div>
        </div>
      )}


      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'48px 0', gap:10, color:'var(--text-muted)' }}>
          <div className="spinner"/> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px 0' }}>
          <Building2 size={36} style={{ opacity:0.3, marginBottom:12 }}/>
          <div style={{ fontWeight:600, marginBottom:6 }}>Aucune société</div>
          <div style={{ fontSize:'0.875rem', color:'var(--text-muted)', marginBottom:20 }}>Commencez par ajouter vos clients et prospects</div>
          <button className="btn btn-primary" onClick={() => navigate('/companies/new')}><Plus size={15}/> Ajouter</button>
        </div>
      ) : (
        <>
        {viewMode === 'grid' ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:16 }}>
            {paged.map(c => {
              const sm = STATUS_META[c.status] || { color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
              return (
                <div key={c.id} onClick={() => navigate(`/companies/${c.id}`)}
                  style={{ background:'var(--bg-card)', border:`2px solid ${selected.has(c.id) ? '#3B82F6' : 'var(--border)'}`, borderRadius:12, padding:'18px 20px',
                    cursor:'pointer', transition:'all 0.15s', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', position:'relative' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.10)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform='none'; }}>
                  {isSuperAdmin && (
                    <div onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}
                      style={{ position:'absolute', top:12, left:12, zIndex:2 }}>
                      <div style={{
                        width:18, height:18, borderRadius:4, border:`2px solid ${selected.has(c.id) ? '#3B82F6' : 'rgba(255,255,255,0.14)'}`,
                        background: selected.has(c.id) ? '#3B82F6' : 'var(--bg-elevated)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                      }}>
                        {selected.has(c.id) && <span style={{ color:'white', fontSize:'0.75rem', lineHeight:1 }}>✓</span>}
                      </div>
                    </div>
                  )}
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:'var(--bg-elevated)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                        {c.logo_url
                          ? <img src={c.logo_url} alt={c.name} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} style={{ width:'100%', height:'100%', objectFit:'contain', padding:4 }}/>
                          : null}
                        <Building2 size={18} color="#64748B" style={{ display: c.logo_url ? 'none' : 'block' }}/>
                      </div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'0.9375rem' }}>{c.name}</div>
                        {c.sector && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{c.sector}</div>}
                      </div>
                    </div>
                    <span style={{ whiteSpace:'nowrap', padding:'3px 10px', borderRadius:20, fontSize:'0.6875rem', fontWeight:700, background:sm.bg, color:sm.color, flexShrink:0 }}>
                      {sm.label || c.status}
                    </span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                    {(c.city || c.country) && (
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.8125rem', color:'var(--text-muted)' }}>
                        <MapPin size={12}/>{[c.city, c.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {c.website && (
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.8125rem', color:'#3B82F6' }}>
                        <Globe size={12}/>{c.website}
                      </div>
                    )}
                    {c.manager_name && (
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.8125rem', color:'var(--text-muted)' }}>
                        <Users size={12}/> {c.manager_name}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8, marginTop:14, paddingTop:12, borderTop:'1px solid var(--border)' }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex:1 }}
                      onClick={e => { e.stopPropagation(); navigate(`/companies/${c.id}`); }}>
                      <Eye size={13}/> Voir
                    </button>
                    <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }}
                      onClick={e => handleDelete(e, c.id)}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {isSuperAdmin && <th style={{ width:36 }}></th>}
                    <th>Société</th>
                    <th>Statut</th>
                    <th>Localisation</th>
                    <th>Secteur</th>
                    <th>Contact</th>
                    <th>Responsable</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(c => {
                    const sm = STATUS_META[c.status] || { color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                    return (
                      <tr key={c.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/companies/${c.id}`)}>
                        {isSuperAdmin && (
                          <td onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}>
                            <div style={{
                              width:16, height:16, borderRadius:3, border:`2px solid ${selected.has(c.id) ? '#3B82F6' : 'rgba(255,255,255,0.14)'}`,
                              background: selected.has(c.id) ? '#3B82F6' : 'var(--bg-elevated)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                              {selected.has(c.id) && <span style={{ color:'white', fontSize:'0.625rem', lineHeight:1 }}>✓</span>}
                            </div>
                          </td>
                        )}
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:30, height:30, borderRadius:7, background:'var(--bg-elevated)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                              {c.logo_url
                                ? <img src={c.logo_url} alt={c.name} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} style={{ width:'100%', height:'100%', objectFit:'contain', padding:3 }}/>
                                : null}
                              <Building2 size={14} color="#94A3B8" style={{ display: c.logo_url ? 'none' : 'block' }}/>
                            </div>
                            <div>
                              <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{c.name}</div>
                              {c.boond_ref && <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>#{c.boond_ref}</div>}
                            </div>
                          </div>
                        </td>
                        <td><span style={{ whiteSpace:'nowrap', padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:sm.bg, color:sm.color }}>{sm.label || c.status}</span></td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{[c.city, c.country].filter(Boolean).join(', ') || '—'}</td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{c.sector || '—'}</td>
                        <td>
                          {c.phone && <PhoneLink phone={c.phone} style={{ display:'block', fontSize:'0.8125rem', color:'#3B82F6' }}/>}
                          {c.website && <div style={{ fontSize:'0.75rem', color:'#3B82F6' }}>{c.website}</div>}
                        </td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{c.manager_name || '—'}</td>
                        <td>
                          <div style={{ display:'flex', gap:4 }}>
                            <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); navigate(`/companies/${c.id}`); }}><Eye size={13}/> Voir</button>
                            <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={e => handleDelete(e, c.id)}><Trash2 size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginTop:16, fontSize:'0.8125rem' }}>
            <button className="btn btn-ghost btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Préc</button>
            <span style={{ color:'var(--text-muted)' }}>Page {page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page>=totalPages} onClick={() => setPage(p=>p+1)}>Suivant →</button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
